/* @format @flow */

import { Map } from 'immutable';
import type { Reducer } from 'redux';
import F from 'lodash/fp';

import { type ActionType, createAction } from '../utils';
import type { FailurePayload } from '../types';

export type Rank = {
  position: number,
  point?: number,
  numberOfPeople?: number,
};

export type Scoreboard = {
  ranks: Rank[],
  me: ?number,
};

export type RankingForCourseContent = Map<string, Scoreboard>;
export type Ranking = {
  +isNewHighScore: boolean,
  +loading: boolean,
  +rankings: Map<string, RankingForCourseContent>,
};

// Actions

const FETCH_RANKINGS_BEGIN: 'proc/ranking/FETCH_RANKINGS_BEGIN' = 'proc/ranking/FETCH_RANKINGS_BEGIN';
const FETCH_RANKINGS_SUCCESS: 'proc/ranking/FETCH_RANKINGS_SUCCESS' = 'proc/ranking/FETCH_RANKINGS_SUCCESS';
const FETCH_RANKINGS_FAILURE: 'proc/ranking/FETCH_RANKINGS_FAILURE' = 'proc/ranking/FETCH_RANKINGS_FAILURE';

type FetchRankInput = {| +uid: string |};
type FetchRankFail = FailurePayload<FetchRankInput>;

export const fetchRankingsBegin = createAction<typeof FETCH_RANKINGS_BEGIN, FetchRankInput>(FETCH_RANKINGS_BEGIN);
export const fetchRankingsSuccess = createAction<
  typeof FETCH_RANKINGS_SUCCESS,
  { uid: string, scoring: any, isNewHighScore?: boolean }
>(FETCH_RANKINGS_SUCCESS);
export const fetchRankingsFailure = createAction<typeof FETCH_RANKINGS_FAILURE, FetchRankFail>(FETCH_RANKINGS_FAILURE);

export type RankingActionType =
  | ActionType<typeof fetchRankingsBegin>
  | ActionType<typeof fetchRankingsSuccess>
  | ActionType<typeof fetchRankingsFailure>;

// Reducer

function convertScoring(scoring: any) {
  const converted = F.pipe(
    F.pick(['japanFiscalYear', 'monthly']),
    F.mapValues(({ leaderboard, me, the99th, the100th }) => {
      const ranks = F.pipe(F.concat([me, the99th, the100th]), F.compact)(leaderboard);
      let { position = null, point = -1 } = me || {};
      if (point < 0) position = null;
      return { ranks, me: position };
    })
  )(scoring);

  return converted;
}

const initialState = {
  isNewHighScore: false,
  loading: false,
  rankings: Map(),
};

const reducer: Reducer<Ranking, RankingActionType> = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_RANKINGS_SUCCESS: {
      const { uid, scoring, isNewHighScore = false } = action.payload;
      return { loading: false, isNewHighScore, rankings: state.rankings.set(uid, Map(convertScoring(scoring))) };
    }
    case FETCH_RANKINGS_BEGIN:
      return { ...state, loading: true };
    case FETCH_RANKINGS_FAILURE:
      return { ...state, loading: false };
    default:
      /*::(action.type: empty);*/
      return state;
  }
};

export default reducer;

// Selectors

export const getIsRankingLoading = (state: Ranking) => state.loading;
